/**
 * Token Storage and Management Utilities
 * For SSO across RoleRelay domains using token-based authentication
 */

const TOKEN_STORAGE_KEY = 'rr_auth_tokens';

export interface AuthTokens {
    idToken: string;
    accessToken: string;
    refreshToken?: string;
    userId: string;
    email: string;
    expiresAt: string;
}

/**
 * Store authentication tokens in sessionStorage
 * Uses sessionStorage (not localStorage) for better security - tokens cleared when tab closes
 */
export const storeTokens = (tokens: AuthTokens): void => {
    try {
        sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
    } catch (error) {
        console.error('Failed to store tokens:', error);
    }
};

/**
 * Retrieve stored authentication tokens
 * If tokens are expired but refresh token exists, triggers a refresh
 */
export const getStoredTokens = (): AuthTokens | null => {
    try {
        const stored = sessionStorage.getItem(TOKEN_STORAGE_KEY);
        if (!stored) {
            return null;
        }

        const tokens: AuthTokens = JSON.parse(stored);

        // Check if tokens are expired
        if (tokens.expiresAt && new Date(tokens.expiresAt) < new Date()) {
            // If we have a refresh token, we could potentially refresh
            // For now, clear tokens and force re-login
            // A more sophisticated implementation would call Cognito to refresh
            clearTokens();
            return null;
        }

        return tokens;
    } catch (error) {
        console.error('Failed to retrieve tokens:', error);
        return null;
    }
};

/**
 * Clear stored tokens (on logout)
 */
export const clearTokens = (): void => {
    try {
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (error) {
        console.error('Failed to clear tokens:', error);
    }
};

/**
 * Get the ID token for Authorization header
 */
export const getIdToken = (): string | null => {
    try {
        const tokens = getStoredTokens();
        return tokens?.idToken || null;
    } catch (error) {
        console.error('Error getting ID token:', error);
        return null;
    }
};

/**
 * Check if user has valid tokens
 */
export const hasValidTokens = (): boolean => {
    return getStoredTokens() !== null;
};

/**
 * Parse tokens from URL hash (after redirect from auth)
 * Expected format: #id_token=...&access_token=...&user_id=...&email=...&expires_at=...
 */
export const parseTokensFromHash = (): AuthTokens | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const hash = window.location.hash.substring(1); // Remove #
    if (!hash) {
        return null;
    }

    const params = new URLSearchParams(hash);

    const idToken = params.get('id_token');
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const userId = params.get('user_id');
    const email = params.get('email');
    const expiresAt = params.get('expires_at');

    if (!idToken || !accessToken || !userId || !email || !expiresAt) {
        return null;
    }

    return {
        idToken,
        accessToken,
        refreshToken: refreshToken || undefined,
        userId,
        email,
        expiresAt,
    };
};

/**
 * Clear hash from URL after parsing tokens
 */
export const clearHashFromUrl = (): void => {
    if (typeof window !== 'undefined' && window.location.hash) {
        // Use replaceState to remove hash without page reload
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
};
